import { useQuery } from '@tanstack/react-query';
import TaskReview from '../../views/TaskReview';
import { useApiClients } from '../../client/api/useApiClient';
import {
  BulkUploadUserGetResponseDto,
  GetAllRolesResponse,
  JobsStatusEnum,
  Roles,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import { useSearchParams } from 'react-router';
import { useEffect, useRef } from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';
import { UserWithOrgContext } from '../../types/user';

export default function TaskReviewPage() {
  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;
  const { BulkUploadsApi, RolesApi } = useApiClients();
  const [searchParams] = useSearchParams();

  const limit = searchParams.get('limit') || '10';
  const page = searchParams.get('page') || '1';

  const { data, isLoading, refetch } = useQuery<
    BulkUploadUserGetResponseDto,
    Error,
    any
  >({
    queryKey: ['task-review', Number(page), Number(limit), organizationId],
    queryFn: async () => {
      const response = await BulkUploadsApi.queuesControllerGetBulkUpload({
        limit: Number(limit),
        page: Number(page),
        ...(organizationId ? { organizationId: organizationId } : {}),
      });
      return response?.data as BulkUploadUserGetResponseDto;
    },
  });

  const { data: allRoles, isLoading: rolesLoading } =
    useQuery<GetAllRolesResponse>({
      queryKey: ['roles'],
      queryFn: async () => {
        const response = await RolesApi.rolesControllerGetAllRoles();
        return response?.data;
      },
    });

  const roles =
    allRoles?.roles?.map((node: Roles) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const lastSnapshotRef = useRef<any[]>([]);
  const lastChangeTimeRef = useRef<number>(Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      const jobs: BulkUploadUserGetResponseDto['jobs'] = data?.jobs ?? [];

      // ✅ Condition 1: stop if all jobs are done
      const allDone = jobs.every(
        (job: any) =>
          job.status === JobsStatusEnum.Completed ||
          job.status === JobsStatusEnum.Failed,
      );

      if (allDone) {
        clearInterval(interval);
        return;
      }

      // ✅ Condition 2: detect "stuck" jobs
      const snapshot = jobs.map((job) => ({
        id: job.id,
        status: job.status,
        progress: job.progress,
      }));

      const snapshotChanged =
        JSON.stringify(snapshot) !== JSON.stringify(lastSnapshotRef.current);

      if (snapshotChanged) {
        lastSnapshotRef.current = snapshot;
        lastChangeTimeRef.current = Date.now();
      } else {
        const elapsed = Date.now() - lastChangeTimeRef.current;
        if (elapsed >= 5 * 60 * 1000) {
          clearInterval(interval);
          return;
        }
      }

      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [data, refetch]);

  if (rolesLoading) {
    return <Loader />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <TaskReview
        data={data}
        roles={roles}
        taskReviewLoading={isLoading}
        params={{
          limit: Number(limit),
          page: Number(page),
        }}
      />
    </div>
  );
}
